import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { RecruitmentTabs } from '../components/RecruitmentTabs';
import { INTERVIEW_OUTCOME_LABEL, INTERVIEW_STAGES, type InterviewOutcome } from '../constants';
import { useCandidates, useDeleteInterview, useInterviews } from '../queries';
import type { Interview } from '../types';
import { candidateName } from '../types';

const OUTCOME_VARIANT: Record<InterviewOutcome, 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  passed: 'secondary',
  failed: 'destructive',
};

const ALL = 'all';

export function InterviewsPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState(ALL);
  const [candidate, setCandidate] = useState(ALL);
  const { data, isPending, isError, error, isFetching } = useInterviews({
    stage: stage === ALL ? undefined : stage,
    candidate: candidate === ALL ? undefined : Number(candidate),
  });
  const { data: candidatesData } = useCandidates();
  const remove = useDeleteInterview();
  const [toDelete, setToDelete] = useState<Interview | null>(null);

  const interviews = data?.results ?? [];
  const candidates = useMemo(() => candidatesData?.results ?? [], [candidatesData]);
  const candidateLabel = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of candidates) map.set(c.id, candidateName(c));
    return map;
  }, [candidates]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Recruitment</h1>
          <p className="text-muted-foreground text-sm">Scheduled interviews + outcomes.</p>
        </div>
        <Button asChild>
          <Link to="/hrm/recruitment/interviews/new">
            <Plus className="size-4" /> Schedule interview
          </Link>
        </Button>
      </div>

      <RecruitmentTabs />

      <div className="flex flex-wrap items-center gap-2">
        <Select value={candidate} onValueChange={setCandidate}>
          <SelectTrigger className="h-9 w-56">
            <SelectValue placeholder="All candidates" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All candidates</SelectItem>
            {candidates.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {candidateName(c)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="h-9 w-56">
            <SelectValue placeholder="All stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All stages</SelectItem>
            {INTERVIEW_STAGES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(stage !== ALL || candidate !== ALL) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStage(ALL);
              setCandidate(ALL);
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load interviews.'}
        </p>
      ) : (
        <div className="rounded-md border" aria-busy={isFetching}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Candidate</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : interviews.length ? (
                interviews.map((iv) => (
                  <TableRow
                    key={iv.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/hrm/recruitment/interviews/${iv.public_id}`)}
                  >
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="font-medium">
                      {candidateLabel.get(iv.candidate) ?? `#${iv.candidate}`}
                    </TableCell>
                    <TableCell>{iv.stage_name}</TableCell>
                    <TableCell>
                      <Badge variant={OUTCOME_VARIANT[iv.outcome]}>
                        {INTERVIEW_OUTCOME_LABEL[iv.outcome]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Edit interview"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/hrm/recruitment/interviews/${iv.public_id}/edit`);
                          }}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Delete interview"
                          onClick={(e) => {
                            e.stopPropagation();
                            setToDelete(iv);
                          }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground text-center">
                    No interviews scheduled.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete interview?</AlertDialogTitle>
            <AlertDialogDescription>This will remove the scheduled interview.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
